import React, { useEffect, useRef } from 'react'

import {
  Box,
  Button,
  InputAdornment,
  Stack,
  Typography,
  useTheme,
} from '@mui/material'

import { useForm, useWatch } from 'react-hook-form'
import { useFormContext } from '../../context/form'
import {
  getLoanToValue,
  getLoanToValueSharedOwnership,
  getPercentagePurchased,
  percentagePurchaseReverseCalc,
} from '../../utils/number'
import FormCheckbox from '../FormCheckbox'
import FormInput from '../FormInput'
import FormRadio from '../FormRadio'
import FormSelect from '../FormSelect'

const MortgageDetails = () => {
  const theme = useTheme()
  const formContext = useForm()
  const { form, onSubmit } = useFormContext()
  const previousValues = useRef(form.getValues())

  const [isRentFieldEnabled, setIsRentFieldEnabled] = React.useState(false)
  const [isDiscountedPriceEnabled, setIsDiscountedPriceEnabled] =
    React.useState(false)
  const [isShareValueEnabled, setIsShareValueEnabled] = React.useState(false)
  const [isPercentagePurchaseEnabled, setIsPercentagePurchaseEnabled] =
    React.useState(false)
  const [isBuildingUseEnabled, setIsBuildingUseEnabled] = React.useState(false)
  const [isCreditQuestionsEnabled, setIsCreditQuestionsEnabled] =
    React.useState(false)
  const [loanToValue, setLoanToValue] = React.useState(
    getLoanToValue(200000, 120000)
  )
  const [purchasePercent, setPurchasePercent] = React.useState(0)
  const [percentChanged, setPercentChanged] = React.useState(false)
  const percentPurchasedWatched = useWatch({ name: 'percentagePurchased' })

  const renderPropertyValue = () => (
    <FormInput
      fullWidth
      name="propertyValue"
      type="number"
      label={
        form?.getValues()?.businessType?.includes('Right_To_Buy')
          ? 'Expected Valuation'
          : 'Property Value'
      }
      control={form.control}
      startAdornment={
        <InputAdornment position="start">
          <strong>£</strong>
        </InputAdornment>
      }
    />
  )

  const renderLoanRequired = () => (
    <FormInput
      fullWidth
      name="loanRequired"
      type="number"
      label={
        form?.getValues()?.businessType?.includes('Right_To_Buy')
          ? 'Mortgage Required'
          : 'Loan Amount'
      }
      control={form.control}
      startAdornment={
        <InputAdornment position="start">
          <strong>£</strong>
        </InputAdornment>
      }
    />
  )

  const renderRent = () => (
    <FormInput
      fullWidth
      name="rent"
      type="number"
      label="Rent"
      control={form.control}
      startAdornment={
        <InputAdornment position="start">
          <strong>£</strong>
        </InputAdornment>
      }
    />
  )

  const renderTerm = () => (
    <FormInput
      fullWidth
      name="term"
      type="number"
      label="Term (Years)"
      control={form.control}
      endAdornment={
        <InputAdornment position="end">
          <strong>Years</strong>
        </InputAdornment>
      }
    />
  )

  const renderDiscountedPrice = () => (
    <FormInput
      fullWidth
      name="discountedPurchasePrice"
      type="number"
      label="Discounted Price"
      control={form.control}
      startAdornment={
        <InputAdornment position="start">
          <strong>£</strong>
        </InputAdornment>
      }
    />
  )

  const renderShareValue = () => (
    <FormInput
      fullWidth
      name="shareValue"
      type="number"
      label="Share Value"
      control={form.control}
      startAdornment={
        <InputAdornment position="start">
          <strong>£</strong>
        </InputAdornment>
      }
    />
  )

  const renderPercentagePurchased = () => (
    <FormInput
      fullWidth
      name="percentagePurchased"
      type="number"
      label="Percentage Purchased"
      control={form.control}
      endAdornment={
        <InputAdornment position="end">
          <strong>%</strong>
        </InputAdornment>
      }
    />
  )

  const renderBuildingUse = () => (
    <FormSelect
      variant="outlined"
      name="buildingUse"
      control={form.control}
      label="Buy To Let Use"
      items={[
        {
          label: '',
          value: 'No_Selection',
        },
        {
          label: 'HMO (House Multiple Occupancy)',
          value: 'HMO',
        },
        {
          label: 'MUB (Multi Unit Building)',
          value: 'MUB',
        },
        {
          label: 'Standard Single Unit Dwelling',
          value: 'StandardSingleUnitDwelling',
        },
        {
          label: 'Ltd Company',
          value: 'Ltd Company',
        },
        {
          label: 'Holiday Let',
          value: 'Holiday_Let',
        },
      ]}
    />
  )

  const renderCreditQuestions = () => (
    <FormCheckbox
      name="creditQuestions"
      label="Credit Questions"
      multiple
      control={form.control}
      items={[
        {
          label: 'Defaults',
          value: 'Defaults',
        },
        {
          label: "CCJ's",
          value: 'CCJs',
        },
        {
          label: "IVA's",
          value: 'IVAs',
        },
        {
          label: 'Mortgage Arrears',
          value: 'Arrears',
        },
      ]}
    />
  )

  React.useEffect(() => {
    const subscription = form.watch((value) => {
      if (value.businessType?.includes('Shared_Ownership')) {
        if (purchasePercent !== value.percentagePurchased) {
          setPercentChanged(true)
        } else {
          setLoanToValue(
            getLoanToValueSharedOwnership(
              value.shareValue!,
              value.loanRequired! || 0
            )
          )
          setPurchasePercent(
            getPercentagePurchased(
              value.shareValue!,
              value.propertyValue! || 0
            ) || 0
          )
        }
      } else {
        setLoanToValue(
          getLoanToValue(value.propertyValue! || 0, value.loanRequired! || 0)
        )
      }
      setIsRentFieldEnabled(value?.businessType?.includes('Buy_To_Let')!)
      setIsBuildingUseEnabled(value?.businessType?.includes('Buy_To_Let')!)
      setIsDiscountedPriceEnabled(
        value?.businessType?.includes('Right_To_Buy')!
      )
      setIsShareValueEnabled(value?.businessType?.includes('Shared_Ownership')!)
      setIsPercentagePurchaseEnabled(
        value?.businessType?.includes('Shared_Ownership')!
      )
      setIsCreditQuestionsEnabled(value?.businessType?.includes('Bad_Credit')!)

      // if (
      //   form.getValues()?.businessType?.includes('Bad_Credit') &&
      //   !previousValues.current.businessType?.includes('Bad_Credit')
      // ) {
      //   previousValues.current = form.getValues()
      //   form.setValue('creditQuestions', ['CCJs', 'Arrears'])
      // }
      previousValues.current = form.getValues()
    })
    return () => subscription.unsubscribe()
  }, [form, purchasePercent])

  useEffect(() => {
    form.setValue(
      'percentagePurchased',
      getPercentagePurchased(
        form.getValues('shareValue') || 0,
        form.getValues('propertyValue')! || 0
      )
    )
  }, [purchasePercent])

  useEffect(() => {
    if (percentChanged) {
      const calcData = percentagePurchaseReverseCalc(
        form.getValues('shareValue')!,
        form.getValues('propertyValue')! || 0,
        percentPurchasedWatched
      )
      form.setValue('shareValue', calcData?.shareValue || 0)
      form.setValue('propertyValue', calcData?.propertyValue || 0)

      setPurchasePercent(
        getPercentagePurchased(
          form.getValues('shareValue')!,
          form.getValues('propertyValue')! || 0
        )
      )
      setPercentChanged(false)
    }
  }, [percentChanged])

  return (
    <Stack spacing={1}>
      <Box
        padding="0px 34px"
        marginTop="20px !important"
        marginBottom="20px !important"
      >
        <Stack spacing={2}>
          <FormRadio
            name="paymentMethod"
            label="Searching:"
            control={form.control}
            items={[
              {
                label: 'Repayment',
                value: 'Repayment',
              },
              {
                label: 'Interest Only',
                value: 'Interest_Only',
              },
            ]}
          />

          <FormSelect
            variant="outlined"
            name="businessType"
            control={form.control}
            label="Mortgage Purpose"
            bold
            items={[
              {
                label: 'Purchase',
                value: 'Standard Purchase',
              },
              {
                label: 'Remortgage',
                value: 'Standard Remortgage',
              },
              {
                label: 'Buy to Let Purchase',
                value: 'Buy_To_Let Purchase',
              },
              {
                label: 'Buy to Let Remortgage',
                value: 'Buy_To_Let Remortgage',
              },
              {
                label: 'Right to Buy Purchase',
                value: 'Right_To_Buy Purchase',
              },
              {
                label: 'Shared Ownership',
                value: 'Shared_Ownership Purchase',
              },
              {
                label: 'Bad Credit Purchase',
                value: 'Bad_Credit Purchase',
              },
              {
                label: 'Bad Credit Remortgage',
                value: 'Bad_Credit Remortgage',
              },
            ]}
          />

          {renderPropertyValue()}

          {renderLoanRequired()}

          {(form.getValues()?.propertyValue! || 0) > 0 &&
            (form?.getValues().loanRequired! || 0) > 0 ? (
            <Box
              padding="10px"
              margin="16px 10px !important"
              sx={{ background: theme.palette.primary.main }}
            >
              <Typography
                color="white"
                variant="body1"
                component="span"
                textAlign="center"
                fontSize={{
                  md: '14px !important',
                  xs: '12px !important',
                }}
                sx={{
                  mb: '0 !important',
                }}
              >
                Your <strong>Loan to Value</strong> is{' '}
                <strong>{loanToValue}%</strong>
              </Typography>
            </Box>
          ) : null}

          {isRentFieldEnabled ? renderRent() : null}

          {isDiscountedPriceEnabled ? renderDiscountedPrice() : null}

          {isShareValueEnabled ? renderShareValue() : null}

          {isPercentagePurchaseEnabled ? renderPercentagePurchased() : null}

          {isBuildingUseEnabled ? renderBuildingUse() : null}

          {isCreditQuestionsEnabled ? renderCreditQuestions() : null}

          {renderTerm()}

          <Button
            color="secondary"
            type="submit"
            variant="contained"
            disableRipple
            sx={{ width: 'fit-content', borderRadius: '20px' }}
          >
            <Typography
              color="white"
              variant="body1"
              component="span"
              textTransform="capitalize"
              fontSize={{
                md: '14px !important',
                xs: '12px !important',
              }}
              sx={{
                mb: '0 !important',
              }}
            >
              Update Results
            </Typography>
          </Button>
        </Stack>
      </Box>
    </Stack>
  )
}

export default MortgageDetails
